/*******************************************************************************
* Licensed Materials - Property of HCL
* (c) Copyright HCL Technologies Ltd. 2020, 2023. All Rights Reserved.

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Text, Clock } from "vr-react-components";
import { DELAY_ANALISYS_ROOM, EXPLORE_ROOM, HOME_ROOM, JOB_ROOM, TOPOLOGY_ROOM, WORKSTATION_ROOM } from "../../rooms/RoomNameConstants";
import { abort, getTopologyRoomData, sendClaraIntent } from "../../multiplayer/SocketConnection";
import { resetExploreRoom } from "../../handlers/ExploreRoomHandlers";
import { previousRoomHandler } from "../../handlers/ChangeRoomHandlers";
import { resetInitialDelayAnalysisRoom } from "../../store/slice/roomSlices/delayAnalysisRoomSlice";
import { resetInitialTopologyRoom } from "../../store/slice/roomSlices/topologyRoomSlice";
import { resetInitialWorkstationRoom } from "../../store/slice/roomSlices/workstationRoomSlice";
import { resetInitialJobRoom } from "../../store/slice/roomSlices/jobRoomSlice";
import { handleLogout } from "../../handlers/RoomHandlers";
import { toggleClara } from "../../store/slice/claraSlice";
import TroubleshootingPanel from "../../multiplayer/TroubleshootingPanel";
import { handleGoToHomeRoom } from "../../handlers/HomeRoomHandlers";

import ClaraPanel from "../../widgets/ClaraPanel";
import store from "../../store/store";
import { getClaraIntent } from "../../handlers/ClaraHandlers";

const Toolbar = React.memo(({
    planeColor = "#121212",
    activeColor = "#363636",
    hoverColor = "#5c5c5c",
    ...otherAttributes 
}) => {
        const dispatch = useDispatch();

        const isClaraEnabled = useSelector((state) => state.clara.isClaraEnabled);
        const isClaraOpen = useSelector((state) => state.clara.isClaraOpen);
        const currentRoom = useSelector((state) => state.room.history[0].ID);
        const currentUserData = useSelector((state) => state.user.data);
        const inVRMode = useSelector((state) => state.private.device.inVRMode);
        const canModify = useSelector((state) => state.private.device.canModify);
        const inCooperative = useSelector((state) => state.private.device.inCooperative);
        const room = useSelector((state) => state.room);
        const claraStatus = useSelector((state) => state.clara.status);

        const [newRoom, setNewRoom] = useState(currentRoom)
        const [isTroubleshootingPanelOpen, setIsTroubleshootingPanelOpen] = useState(false)

        const width = inVRMode ? 6 : 2.5;
        const height = inVRMode ? 0.4 : 0.15;
        const iAmTheOwner = currentUserData.socketId === room.roomCreator?.socketId;

        useEffect(() => {
            if (newRoom !== currentRoom) {
                setNewRoom(currentRoom)
                if ((inCooperative && iAmTheOwner) || !inCooperative) 
                    sendClaraIntent(getClaraIntent());
            }

            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [currentRoom]);

        const handlePreviousRoom = () => {
            reset(currentRoom, dispatch)
            previousRoomHandler()
        }

        const handleToggleClara = () => {
            dispatch(toggleClara())
        }

        return (
            <a-plane
                id="toolbar"
                height={height}
                width={width}
                color={planeColor}
                position={`0 ${inVRMode ? -0.25 : 0.85} ${inVRMode ? -2.5 : -1}`}
                rotation={inVRMode ? "-30 0 0" : "0 0 0"}
                {...otherAttributes}
            >
                <a-entity position={`${-width * 0.45} 0 0.001`}>
                    <Text
                        position={`0 ${height * 0.25} 0`}
                        width={width * 0.4}
                        sizeDecimal={20 / (width / 5)}
                        text={"T-VREX - [ " + currentUserData.username + " ]"}
                    />
                    <Clock
                        width={width * 0.4}
                        textSizeDecimal={25 / (width / 5)}
                        position={`0 ${-height * 0.25} 0`}
                    />
                </a-entity>
                <a-entity class="Icons">
                    {/* PREVIOUS BUTTON CIRCLE */}
                    {(currentRoom !== HOME_ROOM) && (
                        <a-entity
                            position={`${width * 0.05} 0 0.001`}
                            animation__mouseenter={`property: position; to: ${width * 0.05
                                } ${height / 8} ${height / 4
                                };  easing: linear; startEvents: mouseenter; dur: 150`}
                            animation__mouseleave={`property: position; to: ${width * 0.05
                                } 0 0.001; easing: linear; startEvents: mouseleave; dur: 150`}
                        >
                            <a-circle
                                id="back"
                                class={canModify ? "collidable" : ""}
                                radius={height * 0.45}
                                color={activeColor}
                                onClick={handlePreviousRoom}
                                opacity={0.9}
                                animation__mouseenter_color={`property: color; to: ${hoverColor}; easing: linear; startEvents: mouseenter; dur: 150`}
                                animation__mouseleave_color={`property: color; to: ${activeColor}; easing: linear; startEvents: mouseleave; dur: 150`}
                            />
                            <a-image
                                height={height * 0.45}
                                width={height * 0.45}
                                position="0 0 0.001"
                                src="#Back"
                            />
                        </a-entity>
                    )}
                    {/* HOME BUTTON CIRCLE */}
                    <a-entity
                        position={`${width * 0.15} 0 0.001`}
                        animation__mouseenter={`property: position; to: ${width * 0.15} ${height / 8
                            } ${height / 4
                            };  easing: linear; startEvents: mouseenter; dur: 150`}
                        animation__mouseleave={`property: position; to: ${width * 0.15
                            } 0 0.001; easing: linear; startEvents: mouseleave; dur: 150;`}
                    >
                        <a-circle
                            id="home"
                            class={currentRoom !== HOME_ROOM && canModify ? "collidable" : ""}
                            radius={height * 0.45}
                            color={currentRoom !== HOME_ROOM ? activeColor : hoverColor}
                            onClick={() => {
                                abort("goToHomeRoom");
                                handleGoToHomeRoom();
                            }}
                            opacity={0.9}
                            animation__mouseenter_color={`property: color; to: ${hoverColor}; easing: linear; startEvents: mouseenter; dur: 150`}
                            animation__mouseleave_color={`property: color; to: ${currentRoom !== HOME_ROOM ? activeColor : hoverColor
                                }; easing: linear; startEvents: mouseleave; dur: 150`}
                        />
                        <a-image
                            height={height * 0.45}
                            width={height * 0.45}
                            position="0 0 0.001"
                            src="#Home"
                        />
                    </a-entity>
                    {/* SHARE BUTTON CIRCLE */}
                    <a-entity position={`${width * 0.25} 0 0.001`}>
                        <a-entity
                            animation__mouseenter={`property: position; to: 0 ${height / 8
                                } ${height / 4
                                };  easing: linear; startEvents: mouseenter; dur: 150`}
                            animation__mouseleave={`property: position; to: 0 0 0.001; easing: linear; startEvents: mouseleave; dur: 150`}
                        >
                            <a-circle
                                id="share"
                                class="collidable"
                                radius={height * 0.45}
                                color={activeColor}
                                onClick={() => setIsTroubleshootingPanelOpen(old => !old)}
                                opacity={0.9}
                                animation__mouseenter_color={`property: color; to: ${hoverColor}; easing: linear; startEvents: mouseenter; dur: 150`}
                                animation__mouseleave_color={`property: color; to: ${activeColor}; easing: linear; startEvents: mouseleave; dur: 150`}
                            />
                            <a-image
                                height={height * 0.45}
                                width={height * 0.45}
                                position="0 0 0.001"
                                src="#Share"
                            >
                                {room.pendingRequests.length && (
                                    <>
                                        <a-circle
                                            radius={height * 0.2}
                                            color="#E64C21"
                                            position={`${height * 0.4} ${height * 0.3
                                                } 0.001`}
                                        />
                                        <a-text
                                            width={inVRMode ? 2 : 0.9}
                                            height={inVRMode ? 2 : 0.9}
                                            position={inVRMode ?
                                                (room.pendingRequests.length > 9 ? "0.09 0.125 0.1" : "0.11 0.125 0.1") :
                                                (room.pendingRequests.length > 9 ? "-0.03 0.118 0.1" : "-0.023 0.118 0.1")
                                            }
                                            value={room.pendingRequests.length}
                                        />
                                    </>

                                )}
                                {inCooperative && (
                                    <a-circle
                                        radius={height * 0.1}
                                        color="#0366B2"
                                        position={`${-height * 0.34} ${height * 0.3
                                            } 0.001`}
                                    />
                                )}
                            </a-image>
                        </a-entity>
                    </a-entity>
                    {/* CLARA BUTTON CIRCLE */}
                    {!inVRMode ? (<a-entity
                        position={`${width * 0.35} 0 0.001`}
                        animation__mouseenter={`property: position; to: ${width * 0.35
                            } ${height / 8} ${height / 4
                            };  easing: linear; startEvents: mouseenter; dur: 150; enabled: ${isClaraEnabled}`}
                        animation__mouseleave={`property: position; to: ${width * 0.35
                            } 0 0.001; easing: linear; startEvents: mouseleave; dur: 150;`}
                    >
                        <a-circle
                            id="clara"
                            class={inCooperative ? (iAmTheOwner ? "collidable" : "") : "collidable"}
                            radius={height * 0.45}
                            color={isClaraOpen ? hoverColor : activeColor}
                            opacity={0.9}
                            animation__mouseleave_color={`property: color; to: ${isClaraOpen ? hoverColor : activeColor
                                }; startEvents: mouseleave; dur: 150`}
                            animation__mouseenter_color={`property: color; to: ${hoverColor};  easing: linear; startEvents: mouseenter; dur: 150`}
                            onClick={handleToggleClara}
                        />
                        <a-image
                            height={height * 0.9}
                            width={height * 0.9}
                            position={"0 0 0.001"}
                            src={"#" + claraStatus}
                        >
                            {!isClaraEnabled && (
                                <a-circle
                                    radius={height * 0.1}
                                    color="#FF0000"
                                    position={`${height * 0.3} ${height * 0.3
                                        } 0.001`}
                                />
                            )}
                        </a-image>
                    </a-entity>
                    ) : null}
                    <a-plane
                        id="logout"
                        class="collidable"
                        color={planeColor}
                        position={`${width * 0.45} 0 0.001`}
                        height={height}
                        width={width * 0.1}
                        animation__mouseenter={`property: position; to: ${width * 0.45} ${height / 8
                            } ${height / 4
                            };  easing: linear; startEvents: mouseenter; dur: 150`}
                        animation__mouseleave={`property: position; to: ${width * 0.45
                            } 0 0.001; easing: linear; startEvents: mouseleave; dur: 150`}
                        animation__mouseleave_color={`property: color; to: ${planeColor}; startEvents: mouseleave; dur: 150`}
                        animation__mouseenter_color={`property: color; to: ${hoverColor};  easing: linear; startEvents: mouseenter; dur: 150`}
                        onClick={handleLogout}
                    >
                        <a-plane
                            height={height}
                            width={width * 0.001}
                            color="#f2f2f2"
                            position={`${-width * 0.05} 0 0.001`}
                        />
                        <a-image
                            height={height * 0.5}
                            width={height * 0.5}
                            position="0 0 0.001"
                            src="#Logout"
                        />
                    </a-plane>
                </a-entity>
                {isClaraOpen && currentRoom !== EXPLORE_ROOM && (
                    <ClaraPanel
                        width={width / 3}
                        height={inVRMode ? height * 3.41 : height * 6}
                        rotation={inVRMode ? "17 58 25" : "0 0 0"}
                        position={`
                            ${inVRMode ? -4.6 : width / 2 - width / 6}
                            ${(inVRMode ? 2 : (height / 2) + height * 3)}
                            ${inVRMode ? 2.1 : 0}
                        `}
                        onClose={handleToggleClara}
                    />
                )}
                {isTroubleshootingPanelOpen &&
                    <a-plane
                        width={inVRMode ? 2.35 : 1.5}
                        height={inVRMode ? 1.8 : 1}
                        color={"#262626"}
                        opacity={inVRMode ? 0 : 1}
                        position={inVRMode ? "1.66 0.65 0.1" : "0.502 0.58 0"}
                        rotation={inVRMode ? "30 0 0" : "0 0 0"}
                    >
                        <TroubleshootingPanel
                            position={inVRMode ? "-0.015 -0.4 2.67" : "-0.015 -0.92 1.25"}
                            scale={inVRMode ? "0.55 0.55 1" : "0.58 0.58 1"}
                        />
                    </a-plane>
                }
            </a-plane>
        );
    },
);

const reset = (room, dispatch) => {
    switch (room) {
        case EXPLORE_ROOM:
            resetExploreRoom()
            break;
        case DELAY_ANALISYS_ROOM:
            dispatch(resetInitialDelayAnalysisRoom())
            break;
        case TOPOLOGY_ROOM:
            dispatch(resetInitialTopologyRoom())
            // prepare job room data, targetRoom have the reference of redux store
            getTopologyRoomData({
                selectedJob: store.getState().jobroom?.selectedJob,
                targetRoom: TOPOLOGY_ROOM
            });
            break;
        case WORKSTATION_ROOM:
            dispatch(resetInitialWorkstationRoom())
            break;
        case JOB_ROOM:
            dispatch(resetInitialTopologyRoom())
            dispatch(resetInitialJobRoom())
            break;

        default:
            console(room + "not need any reset - maybe request data update?")
            break;
    }

}

export default Toolbar;
